import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Shield, MessageSquare, CreditCard, ArrowRight, CheckCircle, Star, MapPin, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Layout from '../components/Layout';
import { usePageMetadata } from '../hooks/usePageMetadata';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const FEATURED_PROPERTIES = [
  {
    property_id: 'demo1',
    title: 'Sunny Studio in Silver Lake',
    address: '123 Sunset Blvd',
    city: 'Los Angeles',
    price_per_month: 1800,
    bedrooms: 1,
    bathrooms: 1,
    max_guests: 2,
    property_type: 'adu',
    instant_booking: true,
    photos: ['https://images.unsplash.com/photo-1597665863042-47e00964d899?w=800'],
    average_rating: 4.9,
    review_count: 12
  },
  {
    property_id: 'demo2',
    title: 'Cozy Room in Historic Craftsman',
    address: '456 Oak Street',
    city: 'Pasadena',
    price_per_month: 1200,
    bedrooms: 1,
    bathrooms: 1,
    max_guests: 1,
    property_type: 'room',
    instant_booking: false,
    photos: ['https://images.unsplash.com/photo-1757262798677-ab4af4455a58?w=800'],
    average_rating: 4.7,
    review_count: 8
  },
  {
    property_id: 'demo3',
    title: 'Modern Guesthouse with Pool Access',
    address: '789 Palm Drive',
    city: 'Santa Monica',
    price_per_month: 2500,
    bedrooms: 2,
    bathrooms: 1,
    max_guests: 3,
    property_type: 'guesthouse',
    instant_booking: true,
    photos: ['https://images.unsplash.com/photo-1760067537265-dc9f11824eda?w=800'],
    average_rating: 4.8,
    review_count: 15
  }
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Apply page metadata
  usePageMetadata();

  // Fetch LIVE stats from database
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/api/stats/public`);
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
        // Set to null on error - will show "—" in UI
        setStats(null);
      } finally {
        setStatsLoading(false);
      }
    };
    fetchStats();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/browse?q=${encodeURIComponent(searchQuery)}`);
  };

  // Format stats for display
  const formatStat = (value, suffix = '') => {
    if (statsLoading) {
      return <Loader2 className="w-4 h-4 animate-spin inline" />;
    }
    if (value === null || value === undefined) {
      return '—';
    }
    if (typeof value === 'number') {
      return value.toLocaleString() + suffix;
    }
    return value;
  };

  const getAverageRating = () => {
    if (statsLoading) {
      return <Loader2 className="w-4 h-4 animate-spin inline" />;
    }
    if (!stats?.average_rating) {
      return 'New';
    }
    return stats.average_rating.toFixed(1);
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-soft-gray py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Content */}
            <div className="lg:col-span-6 space-y-6">
              <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                <MapPin className="w-4 h-4 text-trust-blue" />
                <span className="text-sm font-medium text-slate-600">Now serving Los Angeles County</span>
              </div>
              
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight">
                Find your next
                <span className="text-trust-blue"> temporary home</span>
              </h1>
              
              <p className="text-lg text-slate-600 leading-relaxed max-w-lg">
                Connect directly with property owners for flexible, month-to-month housing. 
                No long-term leases, no hidden fees.
              </p>

              {/* Search Bar */}
              <form onSubmit={handleSearch} className="flex gap-3 max-w-md">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input 
                    type="text"
                    placeholder="Search by neighborhood or zip..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 h-14 rounded-full border-slate-200"
                    data-testid="hero-search-input"
                  />
                </div>
                <Button 
                  type="submit"
                  className="h-14 px-8 bg-trust-blue hover:bg-trust-blue-hover text-white rounded-full font-semibold"
                  data-testid="hero-search-btn"
                >
                  Search
                </Button>
              </form>

              {/* Stats - LIVE from database */}
              <div className="flex gap-8 pt-4" data-testid="homepage-stats">
                <div>
                  <div className="font-heading text-2xl font-bold text-slate-900" data-testid="stat-listings">
                    {formatStat(stats?.active_listings)}
                  </div>
                  <div className="text-sm text-slate-500">Active Listings</div>
                </div>
                <div>
                  <div className="font-heading text-2xl font-bold text-slate-900" data-testid="stat-renters">
                    {formatStat(stats?.total_renters)}
                  </div>
                  <div className="text-sm text-slate-500">Renters</div>
                </div>
                <div>
                  <div className="font-heading text-2xl font-bold text-slate-900" data-testid="stat-rating">
                    {getAverageRating()}
                  </div>
                  <div className="text-sm text-slate-500">
                    {stats?.total_reviews > 0 ? `Avg Rating (${stats.total_reviews} reviews)` : 'Average Rating'}
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Bento Grid Images */}
            <div className="lg:col-span-6 grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-lg">
                  <img 
                    src="https://images.unsplash.com/photo-1760067537265-dc9f11824eda?w=800" 
                    alt="Modern LA home"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square rounded-2xl overflow-hidden shadow-lg">
                  <img 
                    src="https://images.unsplash.com/photo-1662695093138-d80a86537e9f?w=800" 
                    alt="Happy renter"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="aspect-square rounded-2xl overflow-hidden shadow-lg">
                  <img 
                    src="https://images.unsplash.com/photo-1757262798677-ab4af4455a58?w=800" 
                    alt="Bright living room"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-lg">
                  <img 
                    src="https://images.unsplash.com/photo-1768172230482-26fd6923d1c3?w=800" 
                    alt="LA street"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              How Stay Pair Homes Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Find your perfect temporary home in three simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-soft-gray rounded-2xl p-8 hover:bg-white hover:shadow-lg hover:scale-[1.02] transition-all duration-300 border border-transparent hover:border-slate-100">
              <div className="w-14 h-14 bg-trust-blue rounded-xl flex items-center justify-center mb-6">
                <Search className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
                1. Browse & Discover
              </h3>
              <p className="text-slate-600">
                Search through verified listings in LA County. Filter by price, location, 
                amenities, and move-in date to find your match.
              </p>
            </div>

            <div className="bg-soft-gray rounded-2xl p-8 hover:bg-white hover:shadow-lg hover:scale-[1.02] transition-all duration-300 border border-transparent hover:border-slate-100">
              <div className="w-14 h-14 bg-trust-blue rounded-xl flex items-center justify-center mb-6">
                <MessageSquare className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
                2. Connect & Message
              </h3>
              <p className="text-slate-600">
                Message property owners directly. Ask questions, schedule viewings, 
                and get to know who you'll be renting from.
              </p>
            </div>

            <div className="bg-soft-gray rounded-2xl p-8 hover:bg-white hover:shadow-lg hover:scale-[1.02] transition-all duration-300 border border-transparent hover:border-slate-100">
              <div className="w-14 h-14 bg-trust-blue rounded-xl flex items-center justify-center mb-6">
                <CreditCard className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
                3. Book & Pay Securely
              </h3>
              <p className="text-slate-600">
                Once approved, complete your booking with secure payment. 
                Your deposit and rent are protected through our platform.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Listings */}
      <section className="py-16 md:py-24 bg-soft-gray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Featured Listings
              </h2>
              <p className="text-lg text-slate-600">
                Hand-picked properties with great reviews
              </p>
            </div>
            <Link to="/browse">
              <Button variant="outline" className="hidden md:flex items-center gap-2 rounded-full" data-testid="view-all-btn">
                View all
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURED_PROPERTIES.map((property) => (
              <Link 
                key={property.property_id}
                to={`/browse`}
                className="bg-white rounded-xl border border-slate-100 overflow-hidden hover:shadow-xl hover:border-blue-100 transition-all duration-300 block"
                data-testid={`featured-${property.property_id}`}
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img 
                    src={property.photos[0]} 
                    alt={property.title}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  {property.instant_booking && (
                    <div className="absolute top-3 left-3 bg-trust-blue text-white px-3 py-1 rounded-full text-sm font-medium">
                      Instant Book
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-heading font-semibold text-slate-900 line-clamp-1">
                      {property.title}
                    </h3>
                    <div className="flex items-center gap-1 text-sm shrink-0">
                      <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                      <span className="font-medium">{property.average_rating}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-slate-500 text-sm mb-3">
                    <MapPin className="w-4 h-4" />
                    {property.city}
                  </div>
                  <div className="pt-3 border-t border-slate-100">
                    <span className="font-heading font-bold text-xl text-slate-900">
                      ${property.price_per_month.toLocaleString()}
                    </span>
                    <span className="text-slate-500 text-sm">/month</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-8 text-center md:hidden">
            <Link to="/browse">
              <Button variant="outline" className="rounded-full">
                View all listings
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Built on Trust & Transparency
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Stay Pair Homes connects you directly with property owners. We're not a property manager 
                or guarantor – we're a platform that makes finding and listing temporary housing simple and secure.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center shrink-0">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-slate-900">Verified Profiles</h4>
                    <p className="text-slate-600 text-sm">Email and phone verification for all users</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center shrink-0">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-slate-900">Secure Payments</h4>
                    <p className="text-slate-600 text-sm">All transactions protected through Stripe</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center shrink-0">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-slate-900">Direct Communication</h4>
                    <p className="text-slate-600 text-sm">Message owners directly before booking</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center shrink-0">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-slate-900">Honest Reviews</h4>
                    <p className="text-slate-600 text-sm">Read real reviews from verified renters</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-soft-gray rounded-2xl p-8">
              <Shield className="w-12 h-12 text-trust-blue mb-6" />
              <h3 className="font-heading text-2xl font-bold text-slate-900 mb-4">
                Our Promise
              </h3>
              <p className="text-slate-600 mb-6">
                We facilitate connections between owners and renters but don't make guarantees 
                about properties or tenants. Property owners set their own rules, pricing, and 
                approval requirements. Always communicate directly and do your due diligence.
              </p>
              <div className="flex gap-4">
                <Link to="/register?role=renter">
                  <Button className="bg-trust-blue hover:bg-trust-blue-hover text-white rounded-full" data-testid="find-housing-btn">
                    Find Housing
                  </Button>
                </Link>
                <Link to="/register?role=owner">
                  <Button variant="outline" className="rounded-full" data-testid="list-property-btn">
                    List Property
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-trust-blue">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to find your next home?
          </h2>
          <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of renters and owners who trust Stay Pair Homes for flexible, 
            mid-term housing in Los Angeles County.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/browse">
              <Button size="lg" className="bg-white text-trust-blue hover:bg-slate-100 rounded-full px-8" data-testid="cta-browse-btn">
                Browse Listings
              </Button>
            </Link>
            <Link to="/register?role=owner">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8" data-testid="cta-list-btn">
                List Your Property
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default LandingPage;
